import React, { useState, useEffect } from 'react';
import { groupsAPI } from '../../api/client';
import './Modal.css';

const GroupModal = ({ group, onClose }) => {
  const [formData, setFormData] = useState({
    groupName: '',
    description: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (group) {
      setFormData({
        groupName: group.group_name,
        description: group.description || ''
      });
    }
    setHasChanges(false); // Reset on load
  }, [group]);

  // ESC key to close
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [hasChanges]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => {
    const { name, value } = e.target;

    let normalized;
    if (name === 'groupName') {
      normalized=String(value).toUpperCase();
    }
    else {
      normalized = value;
    }

    setFormData(prev => ({
      ...prev,
      [name]: normalized
    }));
    setHasChanges(true); // Mark as changed
  };

  const handleClose = () => {
    if (hasChanges) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to close?')) {
        return;
      }
    }
    onClose(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!formData.groupName) {
      setError('Group name is required');
      return;
    }
    
    setLoading(true);
    
    try {
      if (group) {
        await groupsAPI.update(group.group_id, formData);
      } else {
        await groupsAPI.create(formData);
      }
      
      setHasChanges(false); // Clear changes flag
      onClose(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save group');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content modal-small">
        <div className="modal-header">
          <h3>{group ? 'Edit Group' : 'Create New Group'}</h3>
          <button 
            className="modal-close" 
            onClick={handleClose}
            type="button"
            title="Close (ESC)"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          {error && (
            <div className="form-error">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}

          {hasChanges && (
            <div className="form-warning">
              <span className="warning-icon">✏️</span>
              You have unsaved changes
            </div>
          )}

          <div className="form-group">
            <label>Group Name *</label>
            <input
              type="text"
              name="groupName"
              value={formData.groupName}
              onChange={handleChange}
              placeholder="e.g., Administrators, Managers"
              required
              autoFocus
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Describe the group's purpose..."
              rows="3"
            />
          </div>

          <div className="modal-footer">
            <button 
              type="button" 
              onClick={handleClose}
              className="btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn-primary"
              disabled={loading}
            >
              {loading ? 'Saving...' : (group ? 'Update Group' : 'Create Group')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GroupModal;